import axios from 'axios';
import qs from 'qs';

import { AuthTokenResponse } from './api.types';
import {
  FORBIDDEN_STATUS_CODE,
  NOT_FOUND_STATUS_CODE,
  UNAUTHORIZED_STATUS_CODE,
} from '../../components/components.constant';
import { ENDPOINT, REACT_APP_API_URI, REACT_APP_AUTH_URL } from '../../config';
import {
  getRefreshToken,
  getWebToken,
  redirectToErrorPage,
  redirectToNotSignedInPage,
  removeWebToken,
  setWebToken,
} from '../auth.service';

export const instance = axios.create({
  baseURL: REACT_APP_API_URI,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
});

instance.interceptors.request.use(
  (config) => {
    const token = getWebToken();

    if (token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

instance.interceptors.response.use(
  async (response) => {
    const refreshToken = getRefreshToken();

    if (refreshToken && response.config.url?.includes('results')) {
      try {
        const { jwtToken } = await axios
          .get<AuthTokenResponse>(`${REACT_APP_AUTH_URL}${ENDPOINT.REFRESH_TOKEN}/${refreshToken}`, {
            withCredentials: true,
          })
          .then((loginResponse) => loginResponse.data);

        setWebToken(jwtToken);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          removeWebToken();
          redirectToNotSignedInPage();
        }
      }
    }

    return response;
  },
  async (error) => {
    // Handle 401/403 errors - user was logged out or token is invalid
    if (axios.isAxiosError(error) && error.response) {
      const { status } = error.response;

      if (status === UNAUTHORIZED_STATUS_CODE) {
        removeWebToken();
        redirectToNotSignedInPage();

        return Promise.reject(error);
      }

      if (status === FORBIDDEN_STATUS_CODE) {
        removeWebToken();
        redirectToErrorPage(FORBIDDEN_STATUS_CODE);

        return Promise.reject(error);
      }

      if (status === NOT_FOUND_STATUS_CODE) {
        redirectToErrorPage(NOT_FOUND_STATUS_CODE);

        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);
